import React from 'react';
import { Trans } from 'react-i18next';

import { AlignedText } from '@actual-app/components/aligned-text';
import { styles } from '@actual-app/components/styles';
import { Tooltip } from '@actual-app/components/tooltip';

import { EnvelopeCellValue } from '#components/budget/envelope/EnvelopeBudgetComponents';
import { CellValueText } from '#components/spreadsheet/CellValue';
import { useFormat } from '#hooks/useFormat';
import { envelopeBudget } from '#spreadsheet/bindings';

import { StatTile } from './StatTile';
import { makeSignedFormatter } from './TotalsList';

type SummaryTilesProps = {
  prevMonthName: string;
};

// The month's totals as a row of cards: the same figures as TotalsList,
// laid out for the wide budget header instead of a stacked list.
export function SummaryTiles({ prevMonthName }: SummaryTilesProps) {
  const format = useFormat();
  const signedFormatter = makeSignedFormatter(format);
  const invertedSignedFormatter = makeSignedFormatter(format, true);

  return (
    <>
      <StatTile label={<Trans>Available funds</Trans>}>
        <Tooltip
          style={{ ...styles.tooltip, lineHeight: 1.5, padding: '6px 10px' }}
          content={
            <>
              <AlignedText
                left={<Trans>Income:</Trans>}
                right={
                  <EnvelopeCellValue
                    binding={envelopeBudget.totalIncome}
                    type="financial"
                  />
                }
              />
              <AlignedText
                left={<Trans>From Last Month:</Trans>}
                right={
                  <EnvelopeCellValue
                    binding={envelopeBudget.fromLastMonth}
                    type="financial"
                  />
                }
              />
            </>
          }
          placement="bottom start"
        >
          <EnvelopeCellValue
            binding={envelopeBudget.incomeAvailable}
            type="financial"
          />
        </Tooltip>
      </StatTile>

      <StatTile label={<Trans>Overspent in {{ prevMonthName }}</Trans>}>
        <EnvelopeCellValue
          binding={envelopeBudget.lastMonthOverspent}
          type="financial"
        >
          {props => <CellValueText {...props} formatter={signedFormatter} />}
        </EnvelopeCellValue>
      </StatTile>

      <StatTile label={<Trans>Budgeted</Trans>}>
        <EnvelopeCellValue
          binding={envelopeBudget.totalBudgeted}
          type="financial"
        >
          {props => <CellValueText {...props} formatter={signedFormatter} />}
        </EnvelopeCellValue>
      </StatTile>

      <StatTile label={<Trans>For next month</Trans>}>
        <EnvelopeCellValue
          binding={envelopeBudget.forNextMonth}
          type="financial"
        >
          {props => (
            <CellValueText {...props} formatter={invertedSignedFormatter} />
          )}
        </EnvelopeCellValue>
      </StatTile>
    </>
  );
}
